/**
 * Mobile Form Input Component
 * Mobile-optimized form inputs with proper keyboard handling
 */

import React, { useRef, useEffect } from 'react';
import { useMobile } from '../contexts/MobileContext';
import { useHapticFeedback } from '../hooks/useHapticFeedback';

const MobileFormInput = ({
  type = 'text',
  label,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  inputMode,
  autoComplete,
  className = '',
  ...props
}) => {
  const mobile = useMobile();
  const { triggerHaptic } = useHapticFeedback();
  const inputRef = useRef(null);

  // Determine inputMode based on type if not provided
  const getInputMode = () => {
    if (inputMode) return inputMode;
    switch (type) {
      case 'tel':
        return 'tel';
      case 'email':
        return 'email';
      case 'number':
        return 'numeric';
      case 'url':
        return 'url';
      default:
        return 'text';
    }
  };

  // Determine autoComplete based on type if not provided
  const getAutoComplete = () => {
    if (autoComplete) return autoComplete;
    switch (type) {
      case 'email':
        return 'email';
      case 'tel':
        return 'tel';
      case 'password':
        return 'current-password';
      default:
        return 'off';
    }
  };

  const handleFocus = () => {
    if (mobile.isMobile) {
      // Scroll input into view on mobile
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  };

  const handleChange = (e) => {
    triggerHaptic('light');
    onChange(e);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        inputMode={getInputMode()}
        autoComplete={getAutoComplete()}
        required={required}
        className={`w-full px-4 py-3 bg-gray-800 border-2 rounded-xl text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none transition-colors ${
          error ? 'border-red-500' : 'border-gray-700'
        }`}
        aria-label={label}
        aria-required={required}
        aria-invalid={!!error}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default MobileFormInput;







